import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatAmount, formatDateTime } from '@/lib/venue/format'
import type { ReservationRefundListItemView } from '@/lib/venue/reservationRefundTypes'
import { REFUND_STATUS_TONE, settlementReflectionText } from '@/lib/venue/reservationRefundViewModel'

const COLUMNS = ['대상 예약', '상태', '환불 유형', '환불 금액', '요청 일시', '정산 반영']

function RefundRow({ item }: { item: ReservationRefundListItemView }) {
  return (
    <tr className="border-b border-slateish-200 last:border-b-0">
      <td className="px-3 py-2 text-ink-700">
        <span className="block">{item.reservationNo ?? `예약 #${item.reservationId}`}</span>
        <span className="block text-xs text-slateish-600">{item.courtName}</span>
      </td>
      <td className="px-3 py-2">
        <Badge tone={REFUND_STATUS_TONE[item.status]}>{item.statusLabel}</Badge>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-slateish-600">{item.refundMethodLabel ?? '-'}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-ink-700">
        {formatAmount(item.refundAmount)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-slateish-600">{formatDateTime(item.requestedAt)}</td>
      {/* 🚨 `settlementCycleMonth=null`을 "반영 실패"로 그리지 않는다 — 상태와 함께 읽는다. */}
      <td className="whitespace-nowrap px-3 py-2 text-slateish-600">{settlementReflectionText(item)}</td>
    </tr>
  )
}

/** 환불 처리 결과 목록 표 + "더 보기" 페이지네이션(설계 §2). */
export function ReservationRefundResultListTable({
  items,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: ReservationRefundListItemView[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  if (items.length === 0) {
    return <EmptyState title="조회된 환불 내역이 없습니다" />
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slateish-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
              {COLUMNS.map((column) => (
                <th key={column} scope="col" className="whitespace-nowrap px-3 py-2">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <RefundRow key={item.id} item={item} />
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
            {isFetchingNextPage ? '불러오는 중' : '더 보기'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
